import Navigation from "@/components/Navigation";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Link } from "react-router-dom";
import { useState } from "react";
import { Button } from "@/components/ui/button";

interface BlogPost {
  id: string;
  title: string;
  content: string;
  created_at: string;
  type: string;
  source: string | null;
}

const Blog = () => {
  const { toast } = useToast();
  const [selectedType, setSelectedType] = useState<string | null>(null);

  const { data: posts, isLoading } = useQuery({
    queryKey: ['blog-posts'],
    queryFn: async () => {
      console.log('Fetching blog posts...');
      const { data, error } = await supabase
        .from('news')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching blog posts:', error);
        toast({
          title: "Error",
          description: "Failed to load blog posts. Please try again later.",
          variant: "destructive",
        });
        throw error;
      }

      console.log('Blog posts fetched successfully:', data);
      return data as BlogPost[];
    },
  });

  const types = [...new Set(posts?.map(post => post.type) || [])];
  const filteredPosts = selectedType
    ? posts?.filter(post => post.type === selectedType)
    : posts;

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <div className="pt-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-12">
            <div>
              <h1 className="text-4xl font-serif mb-4">Latest Updates</h1>
              <p className="text-lg text-gray-600">
                Stay informed about the latest treatments, special offers, and wellness tips.
              </p>
            </div>
            <div className="flex gap-2 mt-6 md:mt-0 overflow-x-auto pb-2">
              <Button
                variant={selectedType === null ? "default" : "outline"}
                onClick={() => setSelectedType(null)}
                className="whitespace-nowrap"
              >
                All Posts
              </Button>
              {types.map((type) => (
                <Button
                  key={type}
                  variant={selectedType === type ? "default" : "outline"}
                  onClick={() => setSelectedType(type)}
                  className="capitalize whitespace-nowrap"
                >
                  {type}
                </Button>
              ))}
            </div>
          </div>
          
          {isLoading ? (
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-8 bg-muted rounded w-3/4 mb-2"></div>
                  <div className="h-24 bg-muted rounded"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {filteredPosts?.map((post) => (
                <Link
                  to={`/blog/${post.id}`}
                  key={post.id}
                  className="group"
                >
                  <article className="bg-card rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-all duration-300 h-full">
                    <div className="p-6">
                      <div className="flex items-center gap-2 mb-4">
                        <span className="capitalize px-3 py-1 bg-muted rounded-full text-sm">
                          {post.type}
                        </span>
                        {post.source && (
                          <span className="text-sm text-muted-foreground">
                            via {post.source}
                          </span>
                        )}
                      </div>
                      <h2 className="text-2xl font-serif mb-4 group-hover:text-primary transition-colors">
                        {post.title}
                      </h2>
                      <p className="text-gray-600 mb-4 line-clamp-3">
                        {post.content}
                      </p>
                      <time className="text-sm text-gray-500">
                        {new Date(post.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </time>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Blog;